import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faClock,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faInstagram } from '@fortawesome/free-brands-svg-icons';
import Reveal from '../components/Reveal';
import PageHeader from '../components/PageHeader';
import { useLanguage } from '../context/LanguageContext';
import {
  WHATSAPP_NUMBER, WHATSAPP_URL, PHONE_DISPLAY, PHONE_TEL, EMAIL, INSTAGRAM_URL, MAPS_URL, MAPS_EMBED_URL
} from '../siteInfo';
import './Contact.css';

const Contact = () => {
  const location = useLocation();
  const { t } = useLanguage();

  const serviceOptions = [
    t('svc1_title'), t('svc2_title'), t('svc3_title'), t('svc4_title'),
    t('svc5_title'), t('svc6_title'), t('svc7_title'), t('svc8_title'), t('svc9_title')
  ];

  const trackEvent = (eventName, params = {}) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, params);
    }
  };
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Récupérer le service depuis l'URL et le pré-remplir
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const serviceFromUrl = urlParams.get('service');
    if (serviceFromUrl) {
      setFormData(prev => ({
        ...prev,
        service: decodeURIComponent(serviceFromUrl)
      }));
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Message toujours en français : il est lu par l'artisan (francophone), pas par le visiteur.
    const whatsappMessage = `*Nouvelle demande de devis - SDM Menuiserie*\n\n` +
      `*Nom:* ${formData.nom}\n` +
      `*Email:* ${formData.email}\n` +
      `*Telephone:* ${formData.telephone || 'Non fourni'}\n` +
      `*Service:* ${formData.service}\n\n` +
      `*Message:*\n${formData.message}`;

    // Encoder le message pour l'URL
    const encodedMessage = encodeURIComponent(whatsappMessage);

    // Ouvrir WhatsApp avec le message pre-rempli
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

    trackEvent('contact_form_submit_whatsapp', {
      service: formData.service || 'non_specifie',
      source_page: 'contact'
    });

    // Ouvrir WhatsApp dans un nouvel onglet
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    setIsSubmitted(true);
    setFormData({
      nom: '',
      email: '',
      telephone: '',
      service: '',
      message: ''
    });
  };

  // Vérifier si un service est pré-rempli
  const isServicePrefilled = formData.service && formData.service !== '';

  const handleSocialClick = (network) => {
    trackEvent('social_click', {
      network,
      source_page: 'contact'
    });
  };

  const handleEmergencyCall = () => {
    trackEvent('call_click', {
      type: 'emergency',
      source_page: 'contact'
    });
  };

  return (
    <div className="contact-page">
      <PageHeader
        image="/img/atelier/atelier-route-mbour.webp"
        eyebrow={t('contact_eyebrow')}
        title={t('contact_page_title')}
        subtitle={t('contact_page_subtitle')}
      />

      {/* Raccourcis : la plupart des clients écrivent ou appellent directement */}
      <div className="contact-quick">
        <div className="container contact-quick-grid">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="contact-quick-item" onClick={() => handleSocialClick('whatsapp')}>
            <FontAwesomeIcon icon={faWhatsapp} />
            <span><strong>WhatsApp</strong>{PHONE_DISPLAY}</span>
          </a>
          <a href={PHONE_TEL} className="contact-quick-item" onClick={handleEmergencyCall}>
            <FontAwesomeIcon icon={faPhone} />
            <span><strong>{t('contact_label_phone_title')}</strong>{PHONE_DISPLAY}</span>
          </a>
          <a href={`mailto:${EMAIL}`} className="contact-quick-item">
            <FontAwesomeIcon icon={faEnvelope} />
            <span><strong>{t('contact_label_email_title')}</strong>{EMAIL}</span>
          </a>
        </div>
      </div>

      <section className="section">
        <div className="container contact-grid">
          {/* Formulaire de contact */}
          <Reveal as="div" animation="left" className="contact-form-section">
            <span className="eyebrow">{t('nav_quote_btn')}</span>
            <h2>{t('contact_form_title')}</h2>
            <p>{t('contact_form_subtitle')}</p>

            {isServicePrefilled && (
              <div className="service-prefilled-notice">
                <FontAwesomeIcon icon={faCheckCircle} />
                <p><strong>{t('contact_service_selected')}</strong> {formData.service}</p>
              </div>
            )}

            {isSubmitted ? (
              <div className="success-message">
                <FontAwesomeIcon icon={faCheckCircle} />
                <h3>{t('contact_success_title')}</h3>
                <p>{t('contact_success_desc')}</p>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="nom">{t('contact_label_name')} *</label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      autoComplete="name"
                      placeholder={t('contact_placeholder_name')}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="telephone">{t('contact_label_phone')}</label>
                    <input
                      type="tel"
                      id="telephone"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      autoComplete="tel"
                      placeholder="+221 XX XXX XX XX"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">{t('contact_label_email')} *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    placeholder="votre.email@exemple.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="service">{t('contact_label_service')} *</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t('contact_select_default')}</option>
                    {serviceOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                    <option value={t('contact_option_other')}>{t('contact_option_other')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t('contact_label_message')} *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder={t('contact_placeholder_message')}
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-whatsapp contact-submit">
                  <FontAwesomeIcon icon={faWhatsapp} /> {t('form_submit')}
                </button>
                <p className="form-note">{t('contact_form_note')}</p>
              </form>
            )}
          </Reveal>

          {/* Informations de contact */}
          <Reveal as="aside" animation="right" delay={150} className="contact-info-section">
            <h2>{t('contact_info_heading')}</h2>

            <ul className="contact-info">
              <li>
                <span className="contact-icon"><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                <div>
                  <h3>{t('contact_label_address')}</h3>
                  <p>KM1 Route de Joal<br />Mbour, Sénégal</p>
                </div>
              </li>
              <li>
                <span className="contact-icon"><FontAwesomeIcon icon={faClock} /></span>
                <div>
                  <h3>{t('contact_label_hours')}</h3>
                  <p>{t('contact_hours_weekday')}</p>
                  <p>{t('contact_hours_saturday')}</p>
                  <p>{t('contact_hours_sunday')}</p>
                </div>
              </li>
            </ul>

            <div className="social-contact-section">
              <h3>{t('contact_social_title')}</h3>
              <div className="social-contact-buttons">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-contact-btn"
                  onClick={() => handleSocialClick('whatsapp')}
                >
                  <FontAwesomeIcon icon={faWhatsapp} />
                  <span>WhatsApp</span>
                </a>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-contact-btn"
                  onClick={() => handleSocialClick('instagram')}
                >
                  <FontAwesomeIcon icon={faInstagram} />
                  <span>Instagram</span>
                </a>
              </div>
            </div>

            <div className="emergency-contact">
              <h3>{t('contact_emergency_title')}</h3>
              <p>{t('contact_emergency_text')}</p>
              <a href={PHONE_TEL} className="emergency-phone" onClick={handleEmergencyCall}>
                {PHONE_DISPLAY}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Carte de l'atelier */}
      <section className="section section-alt">
        <div className="container">
          <Reveal as="div" className="section-head">
            <span className="eyebrow">{t('contact_map_atelier')}</span>
            <h2>{t('contact_map_title')}</h2>
          </Reveal>
          <Reveal as="div" className="map-block">
            <div className="map-frame">
              <iframe
                title={t('contact_map_iframe_title')}
                src={MAPS_EMBED_URL}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="map-info">
              <h3>{t('contact_map_howto')}</h3>
              <ol>
                <li>{t('contact_map_step1')}</li>
                <li>{t('contact_map_step2')}</li>
                <li>{t('contact_map_step3')}</li>
                <li>{t('contact_map_step4')}</li>
              </ol>
              <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                {t('contact_map_btn')}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
